import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Chip,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import { useGetTaskTemplatesQuery, useDeleteTaskTemplateMutation } from '../../store/api';
import { extractPagination } from '../../utils/response';
import { toast } from 'react-toastify';
import { SweetAlert } from '../../utils/sweetAlert';
import { buildTaskTemplateRelationParams } from './taskTemplateApiHelpers';

const ellipsize = (v, max = 56) => {
  if (v === undefined || v === null || v === '') return '—';
  const s = String(v);
  return s.length > max ? `${s.slice(0, max)}...` : s;
};

const priorityColor = (priority) => {
  const p = String(priority || '').toLowerCase();
  if (p === 'high') return 'error';
  if (p === 'medium') return 'warning';
  if (p === 'low') return 'success';
  return 'default';
};

const TaskTemplateList = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      per_page: pagination.per_page,
      paginate: 1,
      sort_by: 'id',
      sort_direction: 'desc',
      ...buildTaskTemplateRelationParams(),
      ...(search ? { search } : {}),
    }),
    [pagination.page, pagination.per_page, search]
  );

  const { data, isFetching: loading, error } = useGetTaskTemplatesQuery(queryParams);
  const [deleteTaskTemplate] = useDeleteTaskTemplateMutation();

  const rows = useMemo(() => {
    const list = data?.data?.list ?? data?.list ?? (Array.isArray(data?.data) ? data.data : []);
    return Array.isArray(list) ? list : [];
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const pag = extractPagination(data);
    setPagination((p) => ({ ...p, total: pag.total ?? 0 }));
  }, [data]);

  useEffect(() => {
    if (error) toast.error(error?.data?.message || 'Failed to load task templates');
  }, [error]);

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const goEdit = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/task-templates/edit/${id}`);
  };

  const goView = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/task-templates/view/${id}`);
  };

  const handleDelete = async () => {
    const row = selectedRow;
    if (!row?.id) return;
    handleCloseMenu();
    const result = await SweetAlert.confirm({
      title: 'Delete task template?',
      text: 'This action cannot be undone.',
      confirmButtonText: 'Delete',
      icon: 'warning',
    });
    if (!result.isConfirmed) return;

    setActionLoadingId(row.id);
    try {
      await deleteTaskTemplate(row.id).unwrap();
      toast.success('Task template deleted');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete task template');
    } finally {
      setActionLoadingId(null);
    }
  };

  const colCount = 6;

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
            Task templates
          </Typography>
          <Can permission="task-template-create">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/task-templates/create')}>
              Add task template
            </Button>
          </Can>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search task templates..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 72, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No task templates found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => row.id && navigate(`/task-templates/view/${row.id}`)}
                        sx={{ cursor: row.id ? 'pointer' : 'default' }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 260 }}>
                          <Tooltip title={row.title ?? ''}>
                            <Typography variant="body2" noWrap>
                              {ellipsize(row.title, 42)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.priority ?? '—'}
                            color={priorityColor(row.priority)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 340 }}>
                          <Tooltip title={row.description ?? ''}>
                            <Typography variant="body2" noWrap>
                              {ellipsize(row.description, 68)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{row.author?.name ?? row.author?.username ?? '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.created_at ?? '—'}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()} sx={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                            disabled={actionLoadingId === row.id}
                            aria-label="Actions"
                          >
                            {actionLoadingId === row.id ? <CircularProgress size={20} /> : <MoreVertIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Can permission="task-template-view">
          <MenuItem onClick={goView}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="task-template-edit">
          <MenuItem onClick={goEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="task-template-delete">
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Can>
      </Menu>
    </PageLayout>
  );
};

export default TaskTemplateList;
